import { Container, Grid, GridColumn, Section, Separator, Spinner, Text } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import BoxNotice from '../components/common/BoxNotice';
import BalanceNotices from '../components/invoice/notices/BalanceNotices';
import LimitedMandateNotice from '../components/invoice/notices/LimitedMandateNotice';
import PaperInvoiceNotice from '../components/invoice/notices/PaperInvoiceNotice';
import WarningNotice from '../components/invoice/notices/WarningNotice';
import PreviousInvoices from '../components/invoice/previousinvoices/PreviousInvoices';
import InvoiceSettingsCard from '../components/invoice/settings/InvoiceSettingsCard';
import UnpaidInvoices from '../components/invoice/unpaidinvoices/UnpaidInvoices';
import ZebraPadding from '../components/common/ZebraPadding';
import { INVOICE_DETAILS_PATH } from '../Routes';
import AnalyticsService from '../services/AnalyticsService';
import { InvoicePaymentStore } from '../stores/InvoicePaymentStore';
import MandatesStore from '../stores/MandatesStore';
import { ITranslateStore } from '../stores/TranslateStore';
import InvoiceDetailsStore from '../stores/InvoiceDetailsStore';
import { ModalStore, ModalType } from '../stores/ModalStore';
import { IRouteStore } from '../stores/RouteStore';

export interface IInvoicesProps {
  translateStore?: ITranslateStore;
  notification?: JSX.Element;
  paymentModalOpen?: boolean;
  analyticsService?: AnalyticsService;
  mandatesStore?: MandatesStore;
  invoicePaymentStore?: InvoicePaymentStore;
  invoiceDetailsStore?: InvoiceDetailsStore;
  showInvoiceSettings?: boolean;
  routeStore?: IRouteStore;
  modalStore?: ModalStore;
}

interface IInvoicesState {
  hasError: boolean;
}

@inject('translateStore', 'analyticsService', 'mandatesStore', 'invoicePaymentStore', 'routeStore', 'modalStore', 'invoiceDetailsStore')
@observer
export default class Invoices extends React.Component<IInvoicesProps, IInvoicesState> {
  constructor(props: IInvoicesProps) {
    super(props);
    this.state = { hasError: false };
  }

  public componentDidMount(): void {
    const invoiceNumber = this.props.routeStore.pathName.includes(INVOICE_DETAILS_PATH) ? this.props.routeStore.path?.pop() : undefined;
    this.props.analyticsService.invoicesViewPage();
    this.props.mandatesStore.tryLoadMandate();
    if (this.props.paymentModalOpen) {
      this.props.invoicePaymentStore.payAll();
    } else if (invoiceNumber) {
      this.props.invoiceDetailsStore.activateInvoiceDetailsByInvoiceNumber(invoiceNumber);
    } else if (this.showCardPaymentReturnModal()) {
      this.activateCardPaymentReturnModal();
    }
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ hasError: true });
    console.error(error, errorInfo);
  }

  public render(): JSX.Element {
    return <>{this.content()}</>;
  }

  private content() {
    const translate = this.props.translateStore.translate;
    const Notification = this.props.notification;
    const mandate = this.props.mandatesStore.mandate;

    if (this.state.hasError) {
      return (
        <ZebraPadding>
          <Container>
            <Text>
              {this.pageTitle()}
              <WarningNotice translationKey="invoices.errors.failed-to-load-page" />
            </Text>
          </Container>
        </ZebraPadding>
      );
    }

    if (mandate.isInitialLoading) {
      return (
        <ZebraPadding>
          <Container>
            <Text>
              {this.pageTitle()}
              <Spinner />
            </Text>
          </Container>
        </ZebraPadding>
      );
    }

    if (mandate.isFinished && mandate.payload.billingMandateLevel === 'NONE') {
      return (
        <ZebraPadding>
          <Container>
            <Text>
              {this.pageTitle()}
              <BoxNotice>{translate('shared.notices.no-mandate-notice')}</BoxNotice>
            </Text>
          </Container>
        </ZebraPadding>
      );
    }

    return [
      <Section key="unpaid-invoices">
        <Container>
          <Text>
            <ZebraPadding>
              <LimitedMandateNotice />
              {this.pageTitle()}
              {Notification}
              <BalanceNotices />
              <PaperInvoiceNotice />
              <UnpaidInvoices />
              <Separator color="invisible" spacing="small" />
              <InvoiceSettingsCard showInvoiceSettings={this.props.showInvoiceSettings} />
            </ZebraPadding>
          </Text>
        </Container>
      </Section>,
      <Section key="previous-invoices" backgroundColor="neutral-bg-level-2" pullBottom="footer">
        <Container>
          <Text>
            <ZebraPadding>
              <PreviousInvoices />
            </ZebraPadding>
          </Text>
        </Container>
      </Section>
    ];
  }

  private pageTitle() {
    const translate = this.props.translateStore.translate;
    return (
      <Grid align={['between-xs', 'middle-xs']}>
        <GridColumn width={['xs']}>
          <h1>{translate('invoices.header.title')}</h1>
        </GridColumn>
      </Grid>
    );
  }

  private showCardPaymentReturnModal(): boolean {
    return !!this.props.routeStore.queryParam('cardTransactionReference') && !!this.props.routeStore.queryParam('brand');
  }

  private activateCardPaymentReturnModal(): void {
    this.props.modalStore.openModal(ModalType.InvoicePaymentModal);
  }
}
